import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Plus, Trophy, Copy } from "lucide-react";

export default function Leagues() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: leagues } = useQuery({ queryKey: ["my-leagues"], queryFn: () => api.get("/leagues/mine").then(r => r.data) });
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState("");

  const create = async () => {
    if (!name.trim()) return toast.error("Angiv et navn");
    try {
      const { data } = await api.post("/leagues", { name: name.trim() });
      toast.success(`Liga "${data.name}" oprettet`);
      setName("");
      qc.invalidateQueries({ queryKey: ["my-leagues"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const join = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return toast.error("Indtast kode");
    try {
      const { data } = await api.post(`/leagues/by-code/${code}/join`);
      toast.success("Joinede liga");
      setJoinCode("");
      qc.invalidateQueries({ queryKey: ["my-leagues"] });
      navigate(`/leagues/${data.league_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke joine"); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Link kopieret");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="label-eyebrow">Private ligaer</div>
      <h1 className="hero-title text-4xl sm:text-5xl mt-2">Mine ligaer</h1>
      <p className="mt-4 text-zinc-400 max-w-xl">Konkurrer i private ligaer med vennerne. Del et link, join med en kode.</p>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="card-flat p-6">
          <div className="flex items-center gap-2 mb-4"><Plus size={16} className="text-emerald-400" /> <h2 className="font-display font-bold text-xl">Opret ny liga</h2></div>
          <Label className="label-eyebrow">Navn</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Fx 'Fredagsklubben'" data-testid="league-create-name" />
          <Button onClick={create} className="mt-4 w-full bg-white text-black hover:bg-zinc-200" data-testid="league-create-btn">Opret liga</Button>
        </div>
        <div className="card-flat p-6">
          <div className="flex items-center gap-2 mb-4"><Users size={16} className="text-purple-400" /> <h2 className="font-display font-bold text-xl">Join med kode</h2></div>
          <Label className="label-eyebrow">Kode</Label>
          <Input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="INDIE2027" className="uppercase tracking-widest" data-testid="league-join-code" />
          <Button onClick={join} className="mt-4 w-full bg-white text-black hover:bg-zinc-200" data-testid="league-join-btn">Join</Button>
        </div>
      </div>

      <div className="mt-14">
        <h2 className="font-display font-bold text-2xl mb-4">Dine ligaer ({leagues?.length ?? 0})</h2>
        <div className="grid gap-4">
          {leagues?.map(l => (
            <div key={l.league_id} className="card-flat p-6 flex flex-wrap items-center gap-4" data-testid={`league-row-${l.league_id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-display font-bold text-xl">{l.name}</div>
                <div className="text-sm text-zinc-500 mt-1 flex items-center gap-3">
                  <span className="flex items-center gap-1"><Users size={12} /> {l.member_count} medlem{l.member_count === 1 ? "" : "mer"}</span>
                  <span className="tracking-widest font-mono text-zinc-400">{l.code}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => copyLink(l.code)} data-testid={`league-copy-${l.league_id}`}>
                <Copy size={12} className="mr-1" /> Kopier invite-link
              </Button>
              <Link to={`/leagues/${l.league_id}`}>
                <Button size="sm" className="bg-white text-black hover:bg-zinc-200" data-testid={`league-open-${l.league_id}`}>
                  <Trophy size={12} className="mr-1" /> Åbn liga
                </Button>
              </Link>
            </div>
          ))}
          {(!leagues || leagues.length === 0) && (
            <div className="card-flat p-10 text-center text-zinc-500">Du er ikke i nogen ligaer endnu.</div>
          )}
        </div>
      </div>
    </div>
  );
}
